import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { BookingManager } from '../../lib/booking-manager';
import { LoginManager } from '../../core/login-manager';
import { settings } from '../../config/settings';
import {
    GIORNI_BREVI, MESI, fineMese, formatImporto, formatTime, inizioMese, parseSql, refId, refLabel, toDateKey
} from '../../lib/format';
import { isAdmin, isAdminGlobale } from '../../lib/ruoli';
import { ChiudiModale, useEntityItem } from '../admin/common';
import { apriAllegato, pesoLeggibile } from '../../lib/allegati';
import { eFormattato, pulisci } from '../../lib/richtext';
import Select from '../../components/Select';
import {
    addMonths, addWeeks, analizzaGiorno, consultabile, durateProponibili, inizioSettimana, labelGiorno,
    labelSettimana, minutesToTime, monthKey, monthMatrix, orariPossibili, weekMatrix
} from './availability';
import CalendarioSettimana from './settimana';

/* Stessa soglia del CSS per il telefono. */
const SCHERMO_STRETTO = '(max-width: 767.98px)';

/** Vero su schermo da telefono; segue rotazioni e ridimensionamenti. */
function useSchermoStretto() {
    // una volta sola: matchMedia rende un oggetto nuovo a ogni chiamata
    const [query] = useState(() => (window.matchMedia ? window.matchMedia(SCHERMO_STRETTO) : null));
    const [stretto, setStretto] = useState(() => Boolean(query && query.matches));
    useEffect(() => {
        if (!query) return undefined;
        const aggiorna = e => setStretto(e.matches);
        query.addEventListener('change', aggiorna);
        return () => query.removeEventListener('change', aggiorna);
    }, [query]);
    return stretto;
}

/**
 * Prenotazione della sala living.
 *
 * Il calendario mostra le occupazioni restituite da `get_availability`
 * (prenotazioni attive e blocchi ricorrenti gia' espansi). I calcoli su
 * spazi liberi e orari proponibili stanno in `availability.js`; qui resta
 * il solo lavoro di presentazione e la conferma via `create_booking`, che
 * rivalida comunque tutto lato server dentro una transazione.
 */
export default function Prenota() {
    const [userInfo] = useState(() => LoginManager.getUserInfo());
    // L'amministratore globale ha in elenco le sale di tutti i condomini:
    // senza il nome del condominio due "Sala Living" sono indistinguibili.
    const globale = isAdminGlobale(userInfo);

    const [sale, setSale] = useState([]);
    const [regolamenti, setRegolamenti] = useState([]);
    const [idSala, setIdSala] = useState(0);
    const [mese, setMese] = useState(() => inizioMese(new Date()));
    // 'mese' guarda le giornate, 'settimana' guarda le ore
    const [vista, setVista] = useState('mese');
    const [settimana, setSettimana] = useState(() => inizioSettimana(new Date()));

    const [availability, setAvailability] = useState(null);
    // ore intere, oppure 'giornata' per la giornata intera
    const [durata, setDurata] = useState(null);
    const [giorno, setGiorno] = useState(null);
    // seconda fase del modale: scelta la durata si passa al riepilogo
    const [conferma, setConferma] = useState(false);

    const [loadingSale, setLoadingSale] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errore, setErrore] = useState(null);

    /* ------------------------------------------------- sale e regolamenti -- */

    useEffect(() => {
        let annullato = false;
        (async () => {
            try {
                const res = await DataManager.getItems('sala', 1, settings.config.limit, {
                    field: 'nome',
                    order: 'asc'
                });
                if (annullato) return;
                if (res && res.success === 1) {
                    const attive = (res.body || []).filter(s => parseInt(s.attiva, 10) === 1);
                    setSale(attive);
                    if (attive.length > 0) setIdSala(parseInt(attive[0].id, 10));
                }
                else {
                    setErrore('Impossibile caricare le sale.');
                }

                const resReg = await DataManager.getItems('regolamento', 1, settings.config.limit, {
                    field: 'data_pubblicazione',
                    order: 'desc'
                });
                if (!annullato && resReg && resReg.success === 1) {
                    setRegolamenti((resReg.body || []).filter(r => parseInt(r.attivo, 10) === 1));
                }
            }
            catch (err) {
                if (!annullato) setErrore('Impossibile contattare il server.');
            }
            finally {
                if (!annullato) setLoadingSale(false);
            }
        })();
        return () => { annullato = true; };
    }, []);

    /* ---------------------------------------------------- disponibilita' -- */

    /* Si chiede al server quello che si guarda: il mese intero o i sette
       giorni della settimana, che possono stare a cavallo di due mesi. */
    const giorniSettimana = useMemo(() => weekMatrix(settimana), [settimana]);
    const intervallo = useMemo(() => (
        vista === 'settimana'
            ? { da: toDateKey(giorniSettimana[0]), a: toDateKey(giorniSettimana[6]) }
            : { da: toDateKey(inizioMese(mese)), a: toDateKey(fineMese(mese)) }
    ), [vista, mese, giorniSettimana]);

    const caricaDisponibilita = useCallback(async () => {
        if (!idSala) return;
        setLoading(true);
        setErrore(null);
        try {
            const res = await BookingManager.getAvailability(idSala, intervallo.da, intervallo.a);
            if (res && res.success === 1) {
                setAvailability(res.data);
            }
            else {
                setAvailability(null);
                setErrore((res && res.message) || 'Impossibile caricare la disponibilità.');
            }
        }
        catch (err) {
            setAvailability(null);
            setErrore('Impossibile contattare il server.');
        }
        finally {
            setLoading(false);
        }
    }, [idSala, intervallo]);

    useEffect(() => {
        caricaDisponibilita();
    }, [caricaDisponibilita]);

    /* Aperta una sala, il calendario parte dal primo mese in cui si prenota:
       finche' le prenotazioni non sono aperte (dicembre 2026) il mese in
       corso e' tutto spento. Una volta sola per sala, e solo in avanti: poi
       si naviga liberi. La sala la si legge dalla risposta, non dalla
       tendina, che cambia prima che arrivi la disponibilita' nuova. */
    const salaPosizionata = useRef(null);
    useEffect(() => {
        const idRisposta = availability && availability.sala ? availability.sala.id : null;
        if (!idRisposta || salaPosizionata.current === idRisposta) return;
        salaPosizionata.current = idRisposta;
        const primo = parseSql(availability.prenotabile_dal);
        if (!primo) return;
        if (vista === 'settimana') {
            if (toDateKey(primo) > toDateKey(giorniSettimana[6])) setSettimana(inizioSettimana(primo));
        }
        else if (monthKey(primo) > monthKey(mese)) {
            setMese(inizioMese(primo));
        }
    }, [availability, vista, mese, giorniSettimana]);

    // cambiando sala o periodo la selezione precedente non è più pertinente
    useEffect(() => {
        setGiorno(null);
        setConferma(false);
    }, [idSala, intervallo]);

    /* --------------------------------------------------------- derivati -- */

    const celle = useMemo(() => monthMatrix(mese), [mese]);

    /* Le code di mese altrui che riempiono la prima e l'ultima settimana
       restano fuori: sono spente, e il server non le ha nemmeno mandate. */
    const giorniVisibili = useMemo(() => (
        vista === 'settimana'
            ? giorniSettimana
            : celle.filter(d => d.getMonth() === mese.getMonth())
    ), [vista, giorniSettimana, celle, mese]);

    /** Analisi giorno per giorno del periodo mostrato. */
    const analisi = useMemo(() => {
        const mappa = {};
        if (!availability) return mappa;
        giorniVisibili.forEach(data => {
            const key = toDateKey(data);
            mappa[key] = analizzaGiorno(key, availability);
        });
        return mappa;
    }, [giorniVisibili, availability]);

    const giornoAnalizzato = giorno ? analisi[giorno] : null;

    /**
     * Durate che il giorno aperto concede, ciascuna con la sua tariffa: il
     * prezzo non si sceglie, lo decide quante ore si chiedono.
     */
    const durate = useMemo(
        () => durateProponibili(giornoAnalizzato, availability),
        [giornoAnalizzato, availability]
    );
    const sceltaDurata = durate.find(d => d.value === durata) || null;
    const tariffa = sceltaDurata ? sceltaDurata.tariffa : null;
    const durataMinuti = sceltaDurata ? sceltaDurata.minuti : 0;

    /**
     * Apre un giorno senza nessuna durata segnata: la scelta e' del giorno,
     * non della sessione, e una casella gia' accesa all'apertura si legge
     * come una decisione presa da qualcun altro.
     */
    const apriGiorno = useCallback((key) => {
        setGiorno(key);
        setDurata(null);
        setConferma(false);
    }, []);

    /* ------------------------------------------------ mese e settimana -- */

    const indietro = () => (
        vista === 'settimana' ? setSettimana(s => addWeeks(s, -1)) : setMese(m => addMonths(m, -1))
    );
    const avanti = () => (
        vista === 'settimana' ? setSettimana(s => addWeeks(s, 1)) : setMese(m => addMonths(m, 1))
    );

    /* Cambiando vista si resta dove si stava guardando: dal mese si entra
       nella sua prima settimana — in quello corrente si entra in questa —
       e dalla settimana si esce nel mese che la contiene. */
    const mostraSettimana = () => {
        const adesso = new Date();
        setSettimana(inizioSettimana(monthKey(adesso) === monthKey(mese) ? adesso : mese));
        setVista('settimana');
    };
    const mostraMese = () => {
        setMese(inizioMese(settimana));
        setVista('mese');
    };

    /* Su telefono sette colonne di ore non entrano: la settimana non si
       offre, e se ci si arriva ruotando lo schermo si torna al suo mese. */
    const stretto = useSchermoStretto();
    useEffect(() => {
        if (stretto && vista === 'settimana') {
            setMese(inizioMese(settimana));
            setVista('mese');
        }
    }, [stretto, vista, settimana]);

    /**
     * Orari di inizio proponibili nel giorno, presi da tutti gli spazi liberi:
     * chi prenota sceglie una durata e poi un'ora, gli spazi in cui la
     * giornata e' spezzata sono un dettaglio che non gli serve vedere. Per la
     * giornata intera l'orario e' uno solo, dall'apertura alla chiusura.
     */
    const orariDisponibili = useMemo(() => {
        if (!giornoAnalizzato || !durataMinuti) return [];
        const out = [];
        giornoAnalizzato.liberi.forEach(l => out.push(...orariPossibili(l, durataMinuti)));
        return out.sort((a, b) => a - b);
    }, [giornoAnalizzato, durataMinuti]);

    /**
     * Regolamento della sala scelta: ogni sala ha il suo e il BookingEngine
     * cerca solo quello, senza ripieghi sul condominio.
     */
    const regolamento = useMemo(() => {
        if (!idSala) return null;
        return regolamenti.find(r => refId(r.idSala) === idSala) || null;
    }, [regolamenti, idSala]);

    /* -------------------------------------------------------- prontezza -- */

    /* Il condomino prenota solo se abilitato; chi amministra non ha nessuno
       che debba abilitarlo. Telefono, palazzina e appartamento non servono.
       La sessione e' quella del login: se l'abilitazione e' arrivata dopo li'
       non c'e' ancora, quindi si guarda il record di adesso, e finche' non e'
       arrivato non si avvisa di niente. Se non arriva, resta la sessione. */
    const amministratore = isAdmin(userInfo);
    const recordFresco = useEntityItem('user', !amministratore && userInfo ? userInfo.id : null);
    const io = recordFresco.item || userInfo;
    const verificato = amministratore || !recordFresco.loading;

    const nonAbilitato = verificato && !amministratore && Boolean(io) && parseInt(io.abilitato, 10) !== 1;
    const puoPrenotare = !nonAbilitato;
    const motivoBlocco = nonAbilitato
        ? 'Il tuo account non è ancora stato abilitato dall\'amministratore: puoi consultare il calendario ma non confermare prenotazioni.'
        : null;

    const oggi = toDateKey(new Date());
    const sala = availability ? availability.sala : null;

    /* `get_availability` non riporta il condominio: lo si legge dal record
       della sala, che l'API restituisce con l'entita' collegata risolta. */
    const condominioSala = useMemo(() => {
        if (!globale) return '';
        const record = sale.find(s => parseInt(s.id, 10) === idSala);
        return record ? refLabel(record.idUserGroup, ['name'], 'Condominio') : '';
    }, [globale, sale, idSala]);

    /** Sale raggruppate per condominio: solo per l'amministratore globale. */
    const saleRaggruppate = useMemo(() => {
        if (!globale) return null;
        const gruppi = new Map();
        sale.forEach(s => {
            const nome = refLabel(s.idUserGroup, ['name'], 'Senza condominio');
            if (!gruppi.has(nome)) gruppi.set(nome, []);
            gruppi.get(nome).push(s);
        });
        if (gruppi.size < 2) return null;
        return Array.from(gruppi.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [globale, sale]);

    return (
        <main className="lv-shell lv-stack lv-cal-page">
            {nonAbilitato && (
                <div className="lv-note lv-note-warn">
                    <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                    Il tuo account non è ancora stato abilitato dall&apos;amministratore: puoi consultare
                    il calendario ma non confermare prenotazioni.
                </div>
            )}

            <section className="lv-card lv-card-pad lv-cal-card">
                <div className="lv-card-head">
                    <div>
                        <p className="lv-eyebrow">Disponibilità sala</p>
                        <h2 className="lv-title lv-title-lg">Calendario</h2>
                        {sala && (
                            <p className="lv-subtitle mb-0">
                                {condominioSala && `${condominioSala} · `}
                                {`${sala.nome} · aperta dalle ${formatTime(sala.orario_apertura)} alle ${formatTime(sala.orario_chiusura)}`}
                            </p>
                        )}
                    </div>
                    {sale.length > 1 && (
                        <Select
                            className="lv-select-inline"
                            ariaLabel="Sala"
                            value={idSala}
                            onChange={setIdSala}
                            options={saleRaggruppate
                                ? saleRaggruppate.map(([condominio, elenco]) => ({
                                    label: condominio,
                                    options: elenco.map(s => ({ value: s.id, label: s.nome }))
                                }))
                                : sale.map(s => ({ value: s.id, label: s.nome }))}
                        />
                    )}
                </div>

                {loadingSale ? (
                    <div className="lv-empty">
                        <div className="lv-empty-icon">
                            <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
                        </div>
                        <p className="mb-0">Caricamento in corso…</p>
                    </div>
                ) : sale.length === 0 ? (
                    <div className="lv-empty">
                        <div className="lv-empty-icon">
                            <FontAwesomeIcon icon={['fas', 'door-closed']} />
                        </div>
                        <p className="mb-0">Nessuna sala è attualmente prenotabile.</p>
                    </div>
                ) : (
                    <>
                        <div className="lv-cal-bar">
                            <div className="lv-month-nav">
                                <button
                                    type="button"
                                    className="lv-btn lv-btn-ghost lv-btn-sm"
                                    aria-label={vista === 'settimana' ? 'Settimana precedente' : 'Mese precedente'}
                                    onClick={indietro}
                                >
                                    <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                                </button>
                                <span className="lv-month-label">
                                    {vista === 'settimana'
                                        ? labelSettimana(settimana)
                                        : `${MESI[mese.getMonth()]} ${mese.getFullYear()}`}
                                </span>
                                <button
                                    type="button"
                                    className="lv-btn lv-btn-ghost lv-btn-sm"
                                    aria-label={vista === 'settimana' ? 'Settimana successiva' : 'Mese successivo'}
                                    onClick={avanti}
                                >
                                    <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                                </button>
                            </div>

                            {!stretto && (
                            <div className="lv-viewswitch" role="group" aria-label="Vista del calendario">
                                <button
                                    type="button"
                                    className={`lv-viewswitch-btn${vista === 'mese' ? ' is-active' : ''}`}
                                    aria-pressed={vista === 'mese'}
                                    onClick={mostraMese}
                                >
                                    Mese
                                </button>
                                <button
                                    type="button"
                                    className={`lv-viewswitch-btn${vista === 'settimana' ? ' is-active' : ''}`}
                                    aria-pressed={vista === 'settimana'}
                                    onClick={mostraSettimana}
                                >
                                    Settimana
                                </button>
                            </div>
                            )}
                        </div>

                        {errore && (
                            <div className="lv-note lv-note-error mb-3">
                                <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                                {errore}
                                <button
                                    type="button"
                                    className="lv-btn lv-btn-ghost lv-btn-sm ms-3"
                                    onClick={caricaDisponibilita}
                                >
                                    Riprova
                                </button>
                            </div>
                        )}

                        {vista === 'settimana' ? (
                            <CalendarioSettimana
                                giorni={giorniSettimana}
                                analisi={analisi}
                                sala={sala || {}}
                                oggi={oggi}
                                giorno={giorno}
                                loading={loading}
                                onApri={apriGiorno}
                            />
                        ) : (
                        <div className={`lv-cal${loading ? ' is-loading' : ''}`}>
                            {GIORNI_BREVI.map(g => (
                                <div key={g} className="lv-cal-head">{g}</div>
                            ))}
                            {celle.map(data => {
                                const key = toDateKey(data);
                                const fuoriMese = data.getMonth() !== mese.getMonth();
                                const info = analisi[key];
                                /* Cosa mostrare delle altrui lo decide get_availability,
                                   che agli altri manda "Occupato". */
                                const siConsulta = consultabile(info, oggi);
                                const classi = [
                                    'lv-cal-cell',
                                    fuoriMese ? 'is-out' : '',
                                    info ? `is-${info.stato}` : '',
                                    info && info.festivo ? 'is-festivo' : '',
                                    giorno === key ? 'is-selected' : '',
                                    key === oggi ? 'is-today' : '',
                                    info && info.mie.length > 0 ? 'is-mine' : ''
                                ].filter(Boolean).join(' ');

                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={classi}
                                        // il contenuto resta il solo numero: gli stati passano dalle classi
                                        title={fuoriMese ? undefined : labelGiorno(key)}
                                        disabled={fuoriMese || !info || (!info.prenotabile && !siConsulta)}
                                        onClick={() => apriGiorno(key)}
                                    >
                                        {data.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                        )}

                        <div className="lv-legend">
                            {vista === 'settimana' ? (
                                <>
                                    <span><span className="lv-swatch is-occupato" /> Occupato</span>
                                    <span><span className="lv-swatch is-mio" /> Una tua prenotazione</span>
                                    <span><span className="lv-swatch is-blocco" /> Chiusa dall&apos;amministratore</span>
                                </>
                            ) : (
                                <>
                                    <span><span className="lv-swatch is-libero" /> Libero</span>
                                    <span><span className="lv-swatch is-parziale" /> In parte occupato</span>
                                    <span><span className="lv-swatch is-pieno" /> Occupato giornata intera</span>
                                    <span><span className="lv-swatch is-festivo" /> Festività</span>
                                </>
                            )}
                        </div>
                    </>
                )}
            </section>

            {giorno && giornoAnalizzato && availability && (
                <GiornoModal
                    sala={sala}
                    giorno={giorno}
                    analisi={giornoAnalizzato}
                    soloConsultazione={!giornoAnalizzato.prenotabile}
                    amministratore={amministratore}
                    tariffe={availability.tariffe || []}
                    durate={durate}
                    durata={durata}
                    onDurata={d => { setDurata(d); setConferma(true); }}
                    tariffa={tariffa}
                    orari={orariDisponibili}
                    puoPrenotare={puoPrenotare}
                    motivoBlocco={motivoBlocco}
                    conferma={conferma}
                    onConferma={setConferma}
                    durataMinuti={durataMinuti}
                    regolamento={regolamento}
                    onClose={() => { setGiorno(null); setConferma(false); }}
                    onConfirmed={() => {
                        setGiorno(null);
                        setConferma(false);
                        caricaDisponibilita();
                    }}
                />
            )}
        </main>
    );
}

/** '1 ora', '3 ore', 'Giornata intera'. */
function etichettaDurata(durata) {
    if (durata === 'giornata') return 'Giornata intera';
    return durata === 1 ? '1 ora' : `${durata} ore`;
}

/**
 * Il giorno scelto sta tutto in un modale: prima le occupazioni e la durata,
 * poi — nella stessa finestra — il riepilogo con orario, motivo e note.
 * Chi prenota dice quante ore gli servono (o la giornata intera) e ognuna
 * porta gia' il suo prezzo. Scelta la durata si passa subito alla seconda
 * fase: gli spazi liberi in cui la giornata e' spezzata diventano le ore
 * proponibili, non una scelta a se'.
 *
 * Dove c'e' un regolamento la conferma non chiude il riepilogo ma apre una
 * terza finestra sopra questa, che e' l'unica da cui si prenota davvero.
 */
function GiornoModal({
    sala, giorno, analisi, soloConsultazione, amministratore, tariffe, durate, durata, onDurata, tariffa,
    orari, puoPrenotare, motivoBlocco, conferma, onConferma, durataMinuti,
    regolamento, onClose, onConfirmed
}) {
    const inConferma = Boolean(!soloConsultazione && conferma && tariffa && orari.length > 0);

    /*
     * Il salvataggio lo fa la seconda fase, ma lo stato sta qui: la X vive in
     * testata, che alle due fasi sopravvive, e mentre la richiesta e' in volo
     * deve essere spenta come lo e' gia' "Indietro".
     */
    const [saving, setSaving] = useState(false);

    return (
        <div className="lv-modal-backdrop" role="dialog" aria-modal="true">
            <div className="lv-modal">
                <div className="lv-modal-head">
                    <div className="lv-modal-head-text">
                        <p className="lv-eyebrow">{sala.nome}</p>
                        <h3 className="lv-title">{labelGiorno(giorno)}</h3>
                    </div>
                    <ChiudiModale onClose={onClose} disabled={saving} />
                </div>

                {inConferma ? (
                    <ConfermaPrenotazione
                        /* cambiando durata il form riparte pulito */
                        key={durata}
                        sala={sala}
                        tariffa={tariffa}
                        giorno={giorno}
                        durata={durata}
                        orari={orari}
                        durataMinuti={durataMinuti}
                        regolamento={regolamento}
                        saving={saving}
                        onSaving={setSaving}
                        onBack={() => onConferma(false)}
                        onClose={onClose}
                        onConfirmed={onConfirmed}
                    />
                ) : (
                    <>
                        <div className="lv-modal-body">
                            {motivoBlocco && (
                                <div className="lv-note lv-note-warn">
                                    <FontAwesomeIcon icon={['fas', 'triangle-exclamation']} className="me-2" />
                                    {motivoBlocco}
                                </div>
                            )}

                            {analisi.occupate.length > 0 && (
                                <div className="lv-occupied">
                                    <p className="lv-label">Già occupato</p>
                                    {analisi.occupate.map((o, i) => (
                                        <div
                                            key={i}
                                            className={`lv-occupied-row${o.tipo === 'blocco' ? ' is-block' : ''}`}
                                        >
                                            <span className="lv-slot-time">
                                                {`${minutesToTime(o.da)} – ${minutesToTime(o.a)}`}
                                            </span>
                                            <span className="lv-slot-note">{o.titolo}</span>
                                            {/* la scheda si apre solo a chi la puo' leggere */}
                                            {o.tipo === 'prenotazione' && o.id && (o.mia || amministratore) && (
                                                <Link
                                                    to={`/prenotazioni/${o.id}`}
                                                    className="lv-btn lv-btn-ghost lv-btn-sm ms-auto"
                                                >
                                                    Dettaglio
                                                </Link>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {soloConsultazione ? (
                                <div className="lv-note">
                                    <FontAwesomeIcon icon={['fas', 'circle-info']} className="me-2" />
                                    Giornata non prenotabile: qui resta il dettaglio di cosa occupa la sala.
                                </div>
                            ) : tariffe.length === 0 ? (
                                <div className="lv-note">
                                    Nessuna tariffa attiva per questa sala: contatta l&apos;amministratore.
                                </div>
                            ) : (
                                <>
                                    <p className="lv-label">Scegli la durata</p>
                                    {durate.length === 0 ? (
                                        <div className="lv-note">
                                            Gli spazi ancora liberi sono troppo brevi per qualunque durata.
                                        </div>
                                    ) : (
                                        <div className="lv-choice-row is-compact is-durate" role="group" aria-label="Durata">
                                            {/* la giornata intera e' la scelta diversa dalle altre: sta
                                                in testa, da sola, su tutta la larghezza */}
                                            {[
                                                ...durate.filter(d => d.value === 'giornata'),
                                                ...durate.filter(d => d.value !== 'giornata')
                                            ].map(d => (
                                                <button
                                                    key={d.value}
                                                    type="button"
                                                    className={[
                                                        'lv-choice',
                                                        d.value === 'giornata' ? 'is-wide' : '',
                                                        d.value === durata ? 'is-selected' : ''
                                                    ].filter(Boolean).join(' ')}
                                                    aria-pressed={d.value === durata}
                                                    disabled={!puoPrenotare}
                                                    onClick={() => onDurata(d.value)}
                                                >
                                                    <span className="lv-choice-title">{etichettaDurata(d.value)}</span>
                                                    <span className="lv-choice-sub">{formatImporto(d.tariffa.prezzo)}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="lv-modal-foot">
                            <button type="button" className="lv-btn lv-btn-ghost" onClick={onClose}>
                                Chiudi
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/*
 * Quanti pixel possono restare sotto il bordo perche' il regolamento si
 * consideri letto fino in fondo: con lo zoom del browser `scrollTop` viene
 * frazionario e l'uguaglianza esatta non arriva mai.
 */
const FONDO_TOLLERANZA = 8;

/**
 * Seconda fase del modale: orario di inizio fra quelli ancora liberi, motivo,
 * partecipanti e note. Non ha guscio proprio, sta dentro `GiornoModal`.
 *
 * Quando la sala ha un regolamento pubblicato la conferma non sta qui: da qui
 * si va avanti a una finestra sua, che apre sopra questa (`RegolamentoModal`)
 * lasciando il modulo montato dietro, cosi' tornando indietro si ritrova
 * scritto. Senza regolamento la fase resta una sola e si conferma di qui.
 */
function ConfermaPrenotazione({
    sala, tariffa, giorno, durata, orari, durataMinuti, regolamento,
    saving, onSaving, onBack, onClose, onConfirmed
}) {
    const [inizioMinuti, setInizioMinuti] = useState(orari[0]);
    const [motivo, setMotivo] = useState('');
    const [partecipanti, setPartecipanti] = useState('');
    const [note, setNote] = useState('');
    const [accetta, setAccetta] = useState(false);
    // terza fase: il regolamento da leggere prima di confermare
    const [alRegolamento, setAlRegolamento] = useState(false);

    const allegato = regolamento && regolamento.allegato && regolamento.allegato.hash
        ? regolamento.allegato
        : null;

    async function scaricaAllegato() {
        try {
            await apriAllegato(allegato);
        }
        catch (err) {
            toast.error(err.message || 'Allegato non recuperabile.', { theme: 'colored' });
        }
    }

    const inizio = `${giorno} ${minutesToTime(inizioMinuti)}:00`;

    /* `capienza` a 0 vuol dire "non indicata": non c'e' nessun tetto da far
       rispettare, e il campo resta un numero libero. */
    const capienza = parseInt(sala.capienza, 10) || 0;

    /* `partecipanti` e' un campo libero: vuoto vuol dire "non dichiarati". */
    const quantiPartecipanti = () => (partecipanti === '' ? null : parseInt(partecipanti, 10));

    /**
     * I controlli che si possono fare senza il server. Stanno a parte perche'
     * li deve passare anche chi va avanti al regolamento: se il motivo manca
     * e' inutile far leggere un testo per poi fermarsi al ritorno.
     */
    function dettagliValidi() {
        if (!motivo.trim()) {
            toast.error('Indica il motivo della richiesta.', { theme: 'colored' });
            return false;
        }
        const quanti = quantiPartecipanti();
        if (quanti !== null && (isNaN(quanti) || quanti < 1)) {
            toast.error('Il numero di partecipanti dev\'essere maggiore di zero.', { theme: 'colored' });
            return false;
        }
        if (quanti !== null && capienza > 0 && quanti > capienza) {
            toast.error(`${sala.nome} ospita al massimo ${capienza} persone.`, { theme: 'colored' });
            return false;
        }
        return true;
    }

    function avanti() {
        if (dettagliValidi()) setAlRegolamento(true);
    }

    async function conferma() {
        if (!dettagliValidi()) return;
        const quanti = quantiPartecipanti();
        onSaving(true);
        try {
            const res = await BookingManager.createBooking({
                idSala: parseInt(sala.id, 10),
                // la tariffa non si manda: il BookingEngine la ricava dalla durata
                ...(durata === 'giornata' ? { giornata: 1 } : { ore: durata }),
                inizio: inizio,
                motivo: motivo.trim(),
                num_partecipanti: quanti,
                note: note,
                accetta_regolamento: accetta
            });

            if (res && res.success === 1) {
                toast.success(res.message || 'Prenotazione confermata.', { theme: 'colored' });
                onConfirmed();
            }
            else {
                toast.error((res && res.message) || 'Prenotazione non riuscita.', { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            onSaving(false);
        }
    }

    return (
        <>
            <div className="lv-modal-body">
                <div className="lv-recap">
                    <div>
                        <span className="lv-recap-label">Quando</span>
                        <span className="lv-recap-value">
                            {`${labelGiorno(giorno)} · ${minutesToTime(inizioMinuti)} – ${minutesToTime(inizioMinuti + durataMinuti)}`}
                        </span>
                    </div>
                    <div>
                        <span className="lv-recap-label">Tariffa</span>
                        <span className="lv-recap-value">
                            {`${etichettaDurata(durata)} · ${tariffa.nome} · ${formatImporto(tariffa.prezzo)}`}
                        </span>
                    </div>
                </div>

                {orari.length > 1 && (
                    <div className="lv-field">
                        <span className="lv-label">Orario di inizio</span>
                        <Select
                            ariaLabel="Orario di inizio"
                            value={inizioMinuti}
                            onChange={setInizioMinuti}
                            options={orari.map(m => ({
                                value: m,
                                label: `${minutesToTime(m)} – ${minutesToTime(m + durataMinuti)}`
                            }))}
                        />
                    </div>
                )}

                <div className="lv-field">
                    <span className="lv-label">
                        Motivo della richiesta<span className="lv-required"> *</span>
                    </span>
                    <input
                        className="lv-input"
                        aria-label="Motivo della richiesta"
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                        placeholder="Es. festa di compleanno, riunione…"
                    />
                </div>
                <div className="lv-field">
                    <span className="lv-label">Numero di partecipanti</span>
                    <input
                        type="number"
                        min="1"
                        max={capienza > 0 ? capienza : undefined}
                        className="lv-input"
                        aria-label="Numero di partecipanti"
                        value={partecipanti}
                        onChange={e => setPartecipanti(e.target.value)}
                    />
                    {capienza > 0 && (
                        <span className="lv-hint">{`La sala ospita al massimo ${capienza} persone.`}</span>
                    )}
                </div>
                <div className="lv-field">
                    <span className="lv-label">Note</span>
                    <textarea
                        className="lv-input"
                        aria-label="Note"
                        rows={3}
                        value={note}
                        onChange={e => setNote(e.target.value)}
                    />
                </div>

            </div>

            <div className="lv-modal-foot">
                <button type="button" className="lv-btn lv-btn-ghost" onClick={onBack} disabled={saving}>
                    Indietro
                </button>
                {regolamento ? (
                    <button type="button" className="lv-btn lv-btn-primary" onClick={avanti}>
                        Avanti
                        <FontAwesomeIcon icon={['fas', 'arrow-right']} className="ms-2" />
                    </button>
                ) : (
                    <button type="button" className="lv-btn lv-btn-primary" onClick={conferma} disabled={saving}>
                        {saving && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                        Conferma prenotazione
                    </button>
                )}
            </div>

            {regolamento && alRegolamento && (
                <RegolamentoModal
                    sala={sala}
                    regolamento={regolamento}
                    allegato={allegato}
                    onAllegato={scaricaAllegato}
                    accetta={accetta}
                    onAccetta={setAccetta}
                    saving={saving}
                    onBack={() => setAlRegolamento(false)}
                    onClose={onClose}
                    onConferma={conferma}
                />
            )}
        </>
    );
}

/**
 * Terza fase: il regolamento della sala, in una finestra sua aperta sopra il
 * modulo. La conferma resta spenta finche' il testo non e' stato scorso fino
 * in fondo — non e' una prova di lettura, ma almeno il testo e' passato tutto
 * sotto gli occhi di chi prenota.
 *
 * La spunta di accettazione invece resta facoltativa com'era: e' quella che fa
 * registrare al backend versione, data e IP, e chi non la mette prenota lo
 * stesso.
 */
function RegolamentoModal({
    sala, regolamento, allegato, onAllegato, accetta, onAccetta,
    saving, onBack, onClose, onConferma
}) {
    const [letto, setLetto] = useState(false);
    const rifTesto = useRef(null);

    const inFondo = (el) => (
        Boolean(el) && el.scrollHeight - el.scrollTop - el.clientHeight <= FONDO_TOLLERANZA
    );

    /* Un regolamento corto sta tutto nel riquadro: non ha un fondo da
       raggiungere, ed e' gia' sotto gli occhi appena la finestra si apre. */
    useEffect(() => {
        if (inFondo(rifTesto.current)) setLetto(true);
    }, []);

    return (
        <div className="lv-modal-backdrop is-stacked" role="dialog" aria-modal="true">
            <div className="lv-modal">
                <div className="lv-modal-head">
                    <div className="lv-modal-head-text">
                        <p className="lv-eyebrow">{sala.nome}</p>
                        <h3 className="lv-title">{regolamento.titolo}</h3>
                    </div>
                    <ChiudiModale onClose={onClose} disabled={saving} />
                </div>

                <div className="lv-modal-body">
                    {/* i regolamenti scritti prima dell'editor sono testo
                        battuto a macchina: restano tali, con i loro a capo */}
                    {eFormattato(regolamento.testo) ? (
                        <div
                            ref={rifTesto}
                            className="lv-regolamento lv-rich is-lettura"
                            onScroll={e => { if (inFondo(e.currentTarget)) setLetto(true); }}
                            dangerouslySetInnerHTML={{ __html: pulisci(regolamento.testo) }}
                        />
                    ) : (
                        <div
                            ref={rifTesto}
                            className="lv-regolamento is-lettura"
                            onScroll={e => { if (inFondo(e.currentTarget)) setLetto(true); }}
                        >
                            {regolamento.testo || 'Testo non disponibile.'}
                        </div>
                    )}
                    {allegato && (
                        <button type="button" className="lv-btn lv-btn-ghost lv-btn-sm" onClick={onAllegato}>
                            <FontAwesomeIcon icon={['fas', 'download']} className="me-2" />
                            {allegato.filename}
                            {allegato.size ? ` · ${pesoLeggibile(allegato.size)}` : ''}
                        </button>
                    )}

                    {/* facoltativa: se spuntata il backend registra versione, data e IP */}
                    <label className="lv-check">
                        <input type="checkbox" checked={accetta} onChange={e => onAccetta(e.target.checked)} />
                        Dichiaro di aver letto e accettato il Regolamento delle Sale Comuni
                        e mi impegno a rispettarne integralmente le disposizioni, assumendomi
                        la responsabilità dei comportamenti dei miei ospiti e di eventuali danni
                        arrecati ai locali o alle attrezzature.
                    </label>

                    {!letto && (
                        <div className="lv-note">
                            <FontAwesomeIcon icon={['fas', 'circle-info']} className="me-2" />
                            Scorri il regolamento fino in fondo: la conferma si sblocca alla fine.
                        </div>
                    )}
                </div>

                <div className="lv-modal-foot">
                    <button type="button" className="lv-btn lv-btn-ghost" onClick={onBack} disabled={saving}>
                        Indietro
                    </button>
                    <button
                        type="button"
                        className="lv-btn lv-btn-primary"
                        onClick={onConferma}
                        disabled={saving || !letto}
                    >
                        {saving && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                        Conferma prenotazione
                    </button>
                </div>
            </div>
        </div>
    );
}
